import { AdjustSticker, StickerIconId } from './adjust.types'
import { measureStickerText, uprightStickerPose } from './adjustStickers'
import { apply as applyMatrix, frameRectOf, imageToFrame, invert } from './adjustGeometry'

import { logger } from 'utils/logging'
import { readLogoFile } from './adjustImageLoading'
import { useAdjustEditor } from './AdjustEditor.context'
import { useCallback } from 'react'

/** Share of the crop's short side a new icon takes, of its height a new text, of its width a new logo */
const ICON_SHARE = 0.14
const TEXT_SHARE = 0.08
const LOGO_SHARE = 0.3

/** Adds stickers at the centre of the visible crop, upright, and selects them */
export const useAdjustStickerActions = () => {
  const { base, doc, apply, assets, stickerColor, stickerFont, setSelectedStickerId, setStickerTab } = useAdjustEditor()

  const add = useCallback((sticker: Omit<AdjustSticker, 'id' | 'x' | 'y' | 'rotation' | 'flipX' | 'opacity'>) => {
    if (!base) return
    const frame = frameRectOf(doc.crop, base.size)
    const centre = applyMatrix(invert(imageToFrame(doc.crop, base.size)), { x: frame.cx, y: frame.cy })
    const pose = uprightStickerPose(doc.crop)
    const id = crypto.randomUUID()
    apply({ type: 'addSticker', sticker: { ...sticker, id, x: centre.x, y: centre.y, rotation: pose.rotation, flipX: pose.flipX, opacity: 1 } })
    setSelectedStickerId(id)
  }, [apply, base, doc.crop, setSelectedStickerId])

  const frameSize = useCallback(() => base ? frameRectOf(doc.crop, base.size) : null, [base, doc.crop])

  const addIcon = useCallback((icon: StickerIconId) => {
    const frame = frameSize()
    if (!frame) return
    const size = Math.min(frame.width, frame.height) * ICON_SHARE
    add({ kind: 'icon', icon, color: stickerColor, width: size, height: size })
  }, [add, frameSize, stickerColor])

  const addText = useCallback((text: string) => {
    const frame = frameSize()
    const trimmed = text.trim()
    if (!frame || !trimmed) return
    const height = frame.height * TEXT_SHARE
    add({ kind: 'text', text: trimmed, font: stickerFont, color: stickerColor, width: measureStickerText(trimmed, stickerFont, height), height })
  }, [add, frameSize, stickerColor, stickerFont])

  const addLogo = useCallback(async (logo: File) => {
    const frame = frameSize()
    if (!frame) return
    try {
      const canvas = await readLogoFile(logo)
      const logoId = crypto.randomUUID()
      assets.registerLogo(logoId, canvas)
      const width = frame.width * LOGO_SHARE
      add({ kind: 'logo', logoId, color: stickerColor, width, height: width * canvas.height / canvas.width })
      setStickerTab('logo')
    } catch (error) {
      logger.warn('Adjust editor: logo upload failed', error, { context: { component: 'PhotoStudio', action: 'adjustAddLogo' } })
    }
  }, [add, assets, frameSize, setStickerTab, stickerColor])

  return { addIcon, addText, addLogo }
}
